import states from 'util/states_titlecase.json';
import BudgetIcon from 'app/shared-components/icons/BudgetIcon';
import ProgressButton from 'app/shared-components/ui/ProgressButton';
import ResponsiveButtonContainer from 'app/shared-components/layout/ResponsiveButtonContainer';
import { CardGroup, HNCardFormFactor, MerchantSpendRule, PaidolUser, Role } from 'API';
import { useSnackbar } from 'notistack';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getMerchantSpendRules, resetSpendRulesSlice } from 'app/pages/store/spendRulesSlice';
import { formatText, sanitizePostalCode, formatName } from 'util/sanitizeText';
import { getAllUsersNoLimitDebounced, selectUsersSlice } from 'app/pages/store/usersSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { selectSelectedGroup } from 'app/pages/store/groupsSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useTranslation } from 'react-i18next';
import { useUserRole } from 'app/hooks/useUserRole';
import { yupResolver } from '@hookform/resolvers/yup';
import { captureException } from '@sentry/react';
import WarningModal from 'app/shared-components/ModalActionsCard/WarningModal';
import {
  AccountCircleOutlined as AccountCircleOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  CreditCardOutlined as CreditCardOutlinedIcon,
  Groups as GroupsIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Unstable_Grid2 as Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CARD_TYPES,
  FormType,
  NO_GROUP,
  NO_RULE,
  NO_SHIPPING,
  NewCardFormValues,
  schema,
  getActiveShippingOptions,
  getUspsOnlyShippingOptions,
  isUspsShippingMethod,
} from './newCardUtils';
import { selectFeatureFlags } from 'app/store/featureFlagsSlice';
import { useAddressValidation } from 'util/addressValidation/useAddressValidation';
import AddressCorrectionDialog from 'util/addressValidation/AddressCorrectionDialog';
import { REPLAY_CHROME_CLASS, sentryMask } from 'sentryReplayPrivacy';
import type { GetCardsPayload, HnValidateAddressInput } from 'CDK_API';
import { resetOpenCardDetail, selectSpecificCardSlice } from 'app/pages/store/CardDataSlice/cardDataSlice';
import SpendingByCategory from 'app/pages/cards/spend-rules/CategorySpendRule';
import SpendingByCountry from 'app/pages/cards/spend-rules/CountrySpendRule';
import { ListType } from 'app/pages/cards/spend-rules/utils';
import { formatComponentMessage } from 'util/addressValidation/formatComponentMessage';
import { containsSecondaryDetails } from 'util/addressValidation/line1Validation';
import { useAddressComponentLabels } from 'util/addressValidation/useAddressComponentLabels';

export interface NewCardFormProps {
  formType: FormType;
  loading: boolean;
  groups?: Array<CardGroup | undefined>;
  rules: MerchantSpendRule[];
  onSubmit: (values: NewCardFormValues) => void;
  onCancel: () => void;
  paymentCard?: GetCardsPayload;
  cancelText?: string;
}

function NewCardForm({
  formType,
  loading,
  groups,
  rules,
  onSubmit,
  onCancel,
  paymentCard,
  cancelText,
}: NewCardFormProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { selectedPaidol, selectedPaidolId } = useAppSelector(selectUserCompanies);
  const specificCard = useAppSelector(selectSpecificCardSlice);
  const selectedGroup = useAppSelector(selectSelectedGroup);
  const { allUsers: users } = useAppSelector(selectUsersSlice);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // SEE-946: Secondary admins are never permitted to hold a card per the
  // matrix. Filter strictly by stored role (no flag gate — same reasoning as
  // AssignCardForm: `roles === SECONDARY_ADMINISTRATOR` is a server-side
  // fact; non-flag paidols have zero such records so the filter is a no-op
  // there). This is the *issuance* form (issuePaymentCard mutation),
  // separate from AssignCardForm (assignPaymentCard).
  const assignableUsers = useMemo(
    () => users.filter((u) => u?.roles !== Role.SECONDARY_ADMINISTRATOR),
    [users]
  );

  // SEE-946: Primary and Secondary Admins can issue/edit cards. Name kept
  // for backward compatibility with downstream usages in this file.
  const { canManageUsersAndCards } = useUserRole();
  const userIsAdministrator = canManageUsersAndCards;
  // Read feature flags directly from Redux store instead of useIsEnabled() because
  // NewCardForm renders inside SidesheetProvider, which is a sibling of (not a descendant of)
  // FeatureFlagProvider in the React tree. useIsEnabled relies on FeatureFlagContext which
  // is not available to sidesheet components. See SEE-629 for details.
  const featureFlags = useAppSelector(selectFeatureFlags);
  const useFedExShipping = featureFlags.flags?.['useFedExShipping']?.enabled === true;
  const isAddressValidationEnabled = featureFlags.flags?.['enableAddressValidation']?.enabled === true;

  const ADDRESS_COMPONENT_LABELS = useAddressComponentLabels();
  const theme = useTheme();
  const [selectedUser, setSelectedUser] = useState<PaidolUser | undefined>(undefined);
  const [isMonthlyLimitWarningOpen, setIsMonthlyLimitWarningOpen] = useState(false);
  const [isBalanceResetWarningOpen, setIsBalanceResetWarningOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<NewCardFormValues | null>(null);

  // Tracks the initial monthly spend limit so the cycle-reset warning can detect a real change.
  // Seeded here for the case where the card's limit is already present on mount; ALSO re-synced in
  // the load effect below for when the limit loads asynchronously (SEE-307 — the one-time seed alone
  // stayed undefined while the field populated async, so isUpdatingMonthlyLimit never fired).
  const initialMonthlySpendLimitRef = useRef<number | undefined>(
    paymentCard?.monthlySpendLimit?.amount ? paymentCard.monthlySpendLimit.amount / 100 : undefined
  );
  const cardholderInputNameRef = useRef(`cardholder_${Math.random()}`);

  const isAgaveClient = useMemo(
    () => selectedPaidol?.isAgaveClient ?? false,
    [selectedPaidol?.isAgaveClient]
  );

  const methods = useForm<NewCardFormValues>({
    mode: 'onBlur',
    defaultValues: {
      formType,
      cardName: paymentCard?.name || '',
      cardHolder: paymentCard?.cardholder?.email || '',
      isAgaveClient,
      cardGroupId: paymentCard?.cardGroup?.id || NO_GROUP,
      cardType:
        formType === FormType.OrderPhysical
          ? HNCardFormFactor.PHYSICAL
          : paymentCard?.formFactor
          ? paymentCard.formFactor
          : HNCardFormFactor.VIRTUAL,
      cardExpirationDate: paymentCard?.expirationDate
        ? new Date(paymentCard.expirationDate)
        : new Date(
            Date.UTC(new Date().getUTCFullYear() + 4, new Date().getUTCMonth(), new Date().getUTCDate())
          ),
      addressFirstName: '',
      addressLastName: '',
      addressZip: '',
      addressStreet1: '',
      addressStreet2: '',
      addressCity: '',
      addressState: '',
      requireSignature: false,
      shippingMethod: NO_SHIPPING,
      isMonthlyBudgetUnlimited: paymentCard?.monthlySpendLimit?.amount === undefined,
      isMaximumTransactionAmountUnlimited: paymentCard?.maximumTransactionAmount?.amount === undefined,
      monthlySpendLimit:
        (paymentCard?.monthlySpendLimit?.amount && paymentCard?.monthlySpendLimit?.amount / 100) || undefined,
      maximumTransactionAmount:
        (paymentCard?.maximumTransactionAmount?.amount &&
          paymentCard?.maximumTransactionAmount?.amount / 100) ||
        undefined,
      merchantSpendRuleId: paymentCard?.spxSpendRule?.id || NO_RULE,
      spendingByCategoryType: ListType.NO_RESTRICTIONS,
      spendingByCategory: [],
      spendingByCountryType: ListType.NO_RESTRICTIONS,
      spendingByCountry: [],
      requireCardCode: false,
      requireAddress: false,
    },
    resolver: yupResolver(schema),
  });

  const { control, formState, getValues, handleSubmit, watch, setValue, clearErrors } = methods;
  const { errors } = formState;
  const formValues = methods.watch();
  const previousMerchantRuleIdRef = useRef<string | undefined>(paymentCard?.spxSpendRule?.id || NO_RULE);

  const unlimitedTransactionWatch = watch('isMaximumTransactionAmountUnlimited');
  const unlimitedSpendingWatch = watch('isMonthlyBudgetUnlimited');
  const monthlySpendLimit = watch('monthlySpendLimit');

  const watchedStreet1 = watch('addressStreet1');
  const watchedStreet2 = watch('addressStreet2');
  const watchedZip = watch('addressZip');
  const watchedCity = watch('addressCity');
  const watchedRegion = watch('addressState');

  const addressForValidation = useMemo<Partial<HnValidateAddressInput>>(
    () => ({
      streetAddress: watchedStreet1 || '',
      extendedAddress: watchedStreet2 || '',
      postalCode: watchedZip || '',
      locality: watchedCity || '',
      region: watchedRegion || '',
      countryCodeAlpha3: 'USA',
    }),
    [watchedStreet1, watchedStreet2, watchedZip, watchedCity, watchedRegion]
  );

  const validation = useAddressValidation(addressForValidation);

  // Local regex catches common "PO Box" formats instantly; HN's label is the authoritative
  // fallback for edge cases the regex misses. Checks both lines because SEE-694 will require
  // PO Box entries to live in line 2.
  const isPoBoxFromAddress = useMemo(() => {
    const re = /p\.?\s*o\.?\s*box/i;
    return re.test(watchedStreet1 || '') || re.test(watchedStreet2 || '');
  }, [watchedStreet1, watchedStreet2]);
  const isPoBoxFromValidator =
    validation.status.kind === 'validated' && validation.status.labels.includes('PO_BOX');
  const isPoBox = isAddressValidationEnabled && (isPoBoxFromAddress || isPoBoxFromValidator);

  const activeShippingOptions = useMemo(() => {
    if (!isAddressValidationEnabled) {
      return getActiveShippingOptions(useFedExShipping);
    }
    const signatureSuffix = t('signatureRequired');
    if (isPoBox) {
      return getUspsOnlyShippingOptions(signatureSuffix);
    }
    return getActiveShippingOptions(useFedExShipping, signatureSuffix);
  }, [isAddressValidationEnabled, isPoBox, useFedExShipping, t]);

  // Check if user is updating an existing monthly spend limit
  const isUpdatingMonthlyLimit = useMemo(() => {
    // Only show warning if:
    // 1. This is an update card form (not creating new)
    // 2. There was an original monthly limit
    // 3. There's a new monthly spend limit value
    // 4. The new value is actually different from the initial value when the form opened
    // 5. The monthly budget is not unlimited
    if (monthlySpendLimit === null || monthlySpendLimit === undefined) {
      return false;
    }

    const initialValue = initialMonthlySpendLimitRef.current;
    if (initialValue === undefined) {
      return false;
    }

    const hasChanged = Math.abs(monthlySpendLimit - initialValue) > 0.01;

    return formType === FormType.UpdateCard && hasChanged && !unlimitedSpendingWatch;
  }, [formType, monthlySpendLimit, unlimitedSpendingWatch]);

  const isCardTypePhysical = useMemo(
    () => formValues.cardType === HNCardFormFactor.PHYSICAL,
    [formValues.cardType]
  );

  const isMerchantSpendRuleSelected = useMemo(
    () =>
      formValues.merchantSpendRuleId !== NO_RULE &&
      formValues.merchantSpendRuleId !== undefined &&
      formValues.merchantSpendRuleId !== '',
    [formValues.merchantSpendRuleId]
  );

  const shouldDisplayMailing = useMemo(
    () => isCardTypePhysical && formType !== FormType.UpdateCard,
    [formType, isCardTypePhysical]
  );

  const fetchAllUsers = useCallback(
    (email?: string) => {
      if (selectedPaidolId) {
        dispatch(getAllUsersNoLimitDebounced({ selectedCompany: selectedPaidolId, email }));
      }
    },
    [dispatch, selectedPaidolId]
  );

  // Helper function to extract category and country restrictions from a merchant rule
  const extractRuleRestrictions = (rule: MerchantSpendRule) => {
    // Normalize arrays first to handle null/undefined safely
    const allowedCategories = rule.merchantCategoryAllowed?.filter(Boolean) ?? [];
    const blockedCategories = rule.merchantCategoryBlocked?.filter(Boolean) ?? [];
    const categoryType =
      allowedCategories.length > 0
        ? ListType.ALLOW_LIST
        : blockedCategories.length > 0
        ? ListType.BLOCK_LIST
        : ListType.NO_RESTRICTIONS;
    const categoryValues = categoryType === ListType.ALLOW_LIST ? allowedCategories : blockedCategories;

    // Extract country type and values
    const allowedCountries = rule.merchantCountryAllowed?.filter(Boolean) ?? [];
    const blockedCountries = rule.merchantCountryBlocked?.filter(Boolean) ?? [];
    const countryType =
      allowedCountries.length > 0
        ? ListType.ALLOW_LIST
        : blockedCountries.length > 0
        ? ListType.BLOCK_LIST
        : ListType.NO_RESTRICTIONS;
    const countryValues = countryType === ListType.ALLOW_LIST ? allowedCountries : blockedCountries;

    return {
      categoryType,
      categoryValues,
      countryType,
      countryValues,
    };
  };

  const populateAddressFromUser = useCallback(
    (paidolUser: PaidolUser) => {
      const firstName = paidolUser?.user?.first_name || '';
      const lastName = paidolUser?.user?.last_name || '';
      try {
        let addressStr = paidolUser?.user?.address || '{}';
        if (addressStr.includes('\\"')) {
          addressStr = addressStr.replace(/\\"/g, '"');
        }
        const parsedAddress = JSON.parse(addressStr);
        setValue('addressFirstName', firstName || '');
        setValue('addressLastName', lastName || '');
        setValue('addressStreet1', parsedAddress.streetAddress || '');
        setValue('addressStreet2', parsedAddress.extendedAddress || '');
        setValue('addressZip', parsedAddress.postalCode || '');
        setValue('addressCity', parsedAddress.locality || '');
        setValue('addressState', parsedAddress.region || '');
      } catch (error) {
        captureException(error, { extra: { address: paidolUser?.user?.address } });
      }
      return { firstName, lastName };
    },
    [setValue]
  );

  // Check if initial card data is still loading (for edit forms only)
  const isInitialDataLoading = useMemo(() => {
    if (formType !== FormType.UpdateCard || !paymentCard) {
      return false;
    }
    // Data is loading if paymentCardDetails hasn't loaded yet
    return !specificCard.paymentCardDetails?.paymentCardDetails;
  }, [formType, paymentCard, specificCard.paymentCardDetails?.paymentCardDetails]);

  // Check if converting from MSR to ISR (triggers balance reset)
  const isConvertingFromMsrToIsr = useMemo(() => {
    // Only check for UpdateCard form type
    if (formType !== FormType.UpdateCard || !paymentCard) {
      return false;
    }

    // Card initially had a merchant spend rule
    const hadInitialMsr = paymentCard.spxSpendRule?.id && paymentCard.spxSpendRule.id !== NO_RULE;

    // Current form selection is "No rule applied"
    const currentMerchantRuleId = formValues.merchantSpendRuleId;
    const isNowNoRule = currentMerchantRuleId === NO_RULE || !currentMerchantRuleId;

    return hadInitialMsr && isNowNoRule;
  }, [formType, paymentCard?.spxSpendRule?.id, formValues.merchantSpendRuleId]);

  // 1. Initialize basic fields (cardGroupId, cardholder, cardType)
  useEffect(() => {
    if ((formType === FormType.UpdateCard || formType === FormType.OrderPhysical) && paymentCard) {
      const currentDirtyFields = formState.dirtyFields;

      // Only set cardGroupId if it hasn't been edited by the user
      if (!currentDirtyFields.cardGroupId) {
        setValue('cardGroupId', paymentCard.cardGroup?.id || NO_GROUP);
      }

      // Only update cardholder if it hasn't been edited by the user
      if (!currentDirtyFields.cardHolder) {
        setValue('cardHolder', paymentCard.cardholder?.email || '');
      }

      // Only update cardType if it hasn't been edited by the user
      // This ensures the correct formFactor is displayed when the form loads after async data fetch
      if (!currentDirtyFields.cardType && paymentCard.formFactor) {
        const cardType =
          formType === FormType.OrderPhysical ? HNCardFormFactor.PHYSICAL : paymentCard.formFactor;
        setValue('cardType', cardType);
      }
    }
  }, [formType, paymentCard, setValue, formState.dirtyFields]);

  // 2. Load the card's individual limits when no merchant rule is selected.
  // Merchant-rule limits are owned by effect #5 below (it keys on the current
  // selection and handles both initial load and switches), so we deliberately
  // don't touch the amount fields here when a rule is selected — that avoids the
  // two effects fighting over the same fields.
  useEffect(() => {
    if ((formType === FormType.UpdateCard || formType === FormType.OrderPhysical) && paymentCard) {
      const currentMerchantRuleId = watch('merchantSpendRuleId');
      const merchantRuleId = currentMerchantRuleId || paymentCard.spxSpendRule?.id;
      const currentDirtyFields = formState.dirtyFields;

      // Check per-field dirty state for limits
      const isMonthlyLimitDirty =
        currentDirtyFields.monthlySpendLimit || currentDirtyFields.isMonthlyBudgetUnlimited;
      const isMaxTransactionDirty =
        currentDirtyFields.maximumTransactionAmount || currentDirtyFields.isMaximumTransactionAmountUnlimited;

      if (!merchantRuleId || merchantRuleId === NO_RULE) {
        // No merchant rule - load from individual card limits - only if not dirty
        if (!isMonthlyLimitDirty) {
          // Re-sync the pristine baseline the cycle-reset warning compares against. This runs while
          // the field is still untouched, so it captures the ORIGINAL limit (not a user edit) even
          // when the card's limit arrives asynchronously — the one-time useRef seed alone misses it.
          initialMonthlySpendLimitRef.current = paymentCard.monthlySpendLimit?.amount
            ? paymentCard.monthlySpendLimit.amount / 100
            : undefined;
          setValue('isMonthlyBudgetUnlimited', paymentCard.monthlySpendLimit?.amount === undefined);
          if (paymentCard.monthlySpendLimit?.amount) {
            setValue('monthlySpendLimit', paymentCard.monthlySpendLimit.amount / 100);
          } else {
            setValue('monthlySpendLimit', undefined);
          }
        }

        if (!isMaxTransactionDirty) {
          setValue(
            'isMaximumTransactionAmountUnlimited',
            paymentCard.maximumTransactionAmount?.amount === undefined
          );
          if (paymentCard.maximumTransactionAmount?.amount) {
            setValue('maximumTransactionAmount', paymentCard.maximumTransactionAmount.amount / 100);
          } else {
            setValue('maximumTransactionAmount', undefined);
          }
        }
      }

      // Set merchantSpendRuleId if not dirty
      if (!formState.isDirty && paymentCard.spxSpendRule?.id) {
        setValue('merchantSpendRuleId', paymentCard.spxSpendRule.id);
      }
    }
  }, [formType, paymentCard, rules, watch, setValue, formState.dirtyFields, formState.isDirty]);

  // 3. Load category/country restrictions (sync from MSR or async from ISR)
  useEffect(() => {
    if ((formType === FormType.UpdateCard || formType === FormType.OrderPhysical) && paymentCard) {
      const currentMerchantRuleId = watch('merchantSpendRuleId');
      // Resolve to the current selection first (matches effect #2). Effect #5 is the
      // authoritative sync on an actual rule switch; this keeps the initial/precedence
      // logic consistent so the two effects can't disagree on whether a rule is selected.
      const merchantRuleId = currentMerchantRuleId || paymentCard.spxSpendRule?.id;

      // Load categories/countries from merchant rule if attached, otherwise from individual rules
      if (merchantRuleId && merchantRuleId !== NO_RULE && rules) {
        // Load from merchant rule
        const merchantRule = rules.find((rule) => rule.id === merchantRuleId);

        if (merchantRule) {
          const { categoryType, categoryValues, countryType, countryValues } =
            extractRuleRestrictions(merchantRule);
          // Only set if form is not dirty (to protect user edits)
          if (!formState.isDirty) {
            setValue('spendingByCategoryType', categoryType);
            setValue('spendingByCategory', categoryValues);
            setValue('spendingByCountryType', countryType);
            setValue('spendingByCountry', countryValues);
          }
        }
      } else {
        // Load existing categories/countries from individual spend rules (only if no merchant rule)
        // Access full card data from specificCard.paymentCardDetails which has attachedSpendRules
        const fullCardData = specificCard.paymentCardDetails?.paymentCardDetails;

        if (fullCardData?.attachedSpendRules?.edges) {
          // Read category/country restrictions directly from the already-fetched card details
          // (no additional Highnote API calls needed — data is already in Redux from HnGetPaymentCardDetails)
          let allCategoryAllowed: string[] = [];
          let allCategoryBlocked: string[] = [];
          let allCountryAllowed: string[] = [];
          let allCountryBlocked: string[] = [];

          fullCardData.attachedSpendRules.edges.forEach((edge: any) => {
            const node = edge.node;
            if (Array.isArray(node?.merchantCategoryAllowed)) {
              allCategoryAllowed = [...allCategoryAllowed, ...node.merchantCategoryAllowed.filter(Boolean)];
            }
            if (Array.isArray(node?.merchantCategoryBlocked)) {
              allCategoryBlocked = [...allCategoryBlocked, ...node.merchantCategoryBlocked.filter(Boolean)];
            }
            if (Array.isArray(node?.merchantCountryAllowed)) {
              allCountryAllowed = [...allCountryAllowed, ...node.merchantCountryAllowed.filter(Boolean)];
            }
            if (Array.isArray(node?.merchantCountryBlocked)) {
              allCountryBlocked = [...allCountryBlocked, ...node.merchantCountryBlocked.filter(Boolean)];
            }
          });

          // Set category form values if restrictions exist
          if (allCategoryAllowed.length > 0 || allCategoryBlocked.length > 0) {
            const currentCategoryType = getValues('spendingByCategoryType');
            const currentCategoryValues = getValues('spendingByCategory');
            const isAtDefault =
              !currentCategoryType ||
              currentCategoryType === ListType.NO_RESTRICTIONS ||
              !currentCategoryValues ||
              currentCategoryValues.length === 0;

            // Only skip if the specific category fields have been edited by the user
            const isCategoryFieldDirty =
              methods.formState.dirtyFields.spendingByCategoryType ||
              methods.formState.dirtyFields.spendingByCategory;

            if (isAtDefault || !isCategoryFieldDirty) {
              const categoryType = allCategoryAllowed.length > 0 ? ListType.ALLOW_LIST : ListType.BLOCK_LIST;
              const categoryValues =
                allCategoryAllowed.length > 0
                  ? [...new Set(allCategoryAllowed)]
                  : [...new Set(allCategoryBlocked)];
              setValue('spendingByCategoryType', categoryType, { shouldDirty: false });
              setValue('spendingByCategory', categoryValues, { shouldDirty: false });
            }
          }

          // Set country form values if restrictions exist
          if (allCountryAllowed.length > 0 || allCountryBlocked.length > 0) {
            const currentCountryType = getValues('spendingByCountryType');
            const currentCountryValues = getValues('spendingByCountry');
            const isAtDefault =
              !currentCountryType ||
              currentCountryType === ListType.NO_RESTRICTIONS ||
              !currentCountryValues ||
              currentCountryValues.length === 0;

            // Only skip if the specific country fields have been edited by the user
            const isCountryFieldDirty =
              methods.formState.dirtyFields.spendingByCountryType ||
              methods.formState.dirtyFields.spendingByCountry;

            if (isAtDefault || !isCountryFieldDirty) {
              const countryType = allCountryAllowed.length > 0 ? ListType.ALLOW_LIST : ListType.BLOCK_LIST;
              const countryValues =
                allCountryAllowed.length > 0
                  ? [...new Set(allCountryAllowed)]
                  : [...new Set(allCountryBlocked)];
              setValue('spendingByCountryType', countryType, { shouldDirty: false });
              setValue('spendingByCountry', countryValues, { shouldDirty: false });
            }
          }
        }
      }
    }
  }, [
    formType,
    paymentCard,
    rules,
    watch,
    specificCard.paymentCardDetails,
    formState.isDirty,
    setValue,
    getValues,
  ]);

  // 4. Populate cardName and shipping address from the existing cardholder (OrderPhysical only)
  useEffect(() => {
    if ((formType === FormType.UpdateCard || formType === FormType.OrderPhysical) && paymentCard) {
      const currentDirtyFields = formState.dirtyFields;

      if (formType === FormType.OrderPhysical && paymentCard.cardholder?.email && users.length > 0) {
        const cardholderUser = users.find((user) => user.email === paymentCard.cardholder?.email);

        if (cardholderUser) {
          const { firstName, lastName } = populateAddressFromUser(cardholderUser);

          // Only set cardName if it hasn't been edited by the user
          if (!currentDirtyFields.cardName) {
            // Set the card name from card or user name
            if (paymentCard.name) {
              setValue('cardName', paymentCard.name);
            } else if (firstName || lastName) {
              setValue('cardName', `${firstName} ${lastName}`.trim());
            }
          }
        }
      } else if (formType === FormType.UpdateCard) {
        // For UpdateCard, only set cardName if it hasn't been edited by the user
        if (!currentDirtyFields.cardName) {
          setValue('cardName', paymentCard.name || '');
        }
      }
    }
  }, [formType, paymentCard, users, setValue, formState.dirtyFields, populateAddressFromUser]);

  // 5. Sync category/country/limit fields when the merchant spend rule selection changes
  useEffect(() => {
    const merchantRuleId = formValues.merchantSpendRuleId;
    const previousMerchantRuleId = previousMerchantRuleIdRef.current;

    if (merchantRuleId && merchantRuleId !== NO_RULE && rules) {
      const merchantRule = rules.find((rule) => rule.id === merchantRuleId);

      if (merchantRule) {
        const { categoryType, categoryValues, countryType, countryValues } =
          extractRuleRestrictions(merchantRule);
        setValue('spendingByCategoryType', categoryType);
        setValue('spendingByCategory', categoryValues);
        setValue('spendingByCountryType', countryType);
        setValue('spendingByCountry', countryValues);

        // Reflect the selected rule's limits. This effect (unlike the limit
        // loader above) keys on the current selection and re-runs on every
        // switch, so it's the reliable place to keep the amount fields in sync
        // with the chosen named rule (SEE-1103/1104 display gap).
        if (merchantRule.monthlyLimitAmount?.value) {
          setValue('monthlySpendLimit', merchantRule.monthlyLimitAmount.value / 100);
          setValue('isMonthlyBudgetUnlimited', false);
        } else {
          setValue('monthlySpendLimit', undefined);
          setValue('isMonthlyBudgetUnlimited', true);
        }

        if (merchantRule.maximumAmount?.value) {
          setValue('maximumTransactionAmount', merchantRule.maximumAmount.value / 100);
          setValue('isMaximumTransactionAmountUnlimited', false);
        } else {
          setValue('maximumTransactionAmount', undefined);
          setValue('isMaximumTransactionAmountUnlimited', true);
        }
      }

      previousMerchantRuleIdRef.current = merchantRuleId;
    } else if (merchantRuleId === NO_RULE || !merchantRuleId) {
      // When detaching MSR, preserve values from the previous merchant rule
      if (previousMerchantRuleId && previousMerchantRuleId !== NO_RULE && rules) {
        const previousMerchantRule = rules.find((rule) => rule.id === previousMerchantRuleId);

        if (previousMerchantRule) {
          // Preserve categories and countries from the merchant rule being detached
          const { categoryType, categoryValues, countryType, countryValues } =
            extractRuleRestrictions(previousMerchantRule);
          setValue('spendingByCategoryType', categoryType);
          setValue('spendingByCategory', categoryValues);
          setValue('spendingByCountryType', countryType);
          setValue('spendingByCountry', countryValues);

          // Preserve limits from the merchant rule being detached
          if (previousMerchantRule.monthlyLimitAmount?.value) {
            setValue('monthlySpendLimit', previousMerchantRule.monthlyLimitAmount.value / 100);
            setValue('isMonthlyBudgetUnlimited', false);
          }
          if (previousMerchantRule.maximumAmount?.value) {
            setValue('maximumTransactionAmount', previousMerchantRule.maximumAmount.value / 100);
            setValue('isMaximumTransactionAmountUnlimited', false);
          }
        }
      } else {
        // Only clear if there was no previous merchant rule (initial load with no rule)
        setValue('spendingByCategoryType', ListType.NO_RESTRICTIONS);
        setValue('spendingByCategory', []);
        setValue('spendingByCountryType', ListType.NO_RESTRICTIONS);
        setValue('spendingByCountry', []);
      }

      previousMerchantRuleIdRef.current = merchantRuleId;
    }
  }, [formValues.merchantSpendRuleId, rules, setValue]);

  // 6. Lazy-set merchantSpendRuleId and cardGroupId after async rules/group data loads
  useEffect(() => {
    // Lazy value set since form is mounted before get rules request

    if (paymentCard) {
      const spendRule = rules?.find((rule) => rule?.paymentCards?.includes(paymentCard.paymentCardId));

      setValue('merchantSpendRuleId', spendRule?.id);
    }
    selectedGroup?.id && setValue('cardGroupId', selectedGroup.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCard?.paymentCardId, paymentCard?.spxSpendRule?.id, rules, selectedGroup?.id, setValue]);

  // 7. Fetch initial data on mount; cleanup on unmount
  useEffect(() => {
    fetchAllUsers();
    dispatch(getMerchantSpendRules({ paidolId: selectedPaidolId, shouldLoadDetails: false }));

    return () => {
      dispatch(resetSpendRulesSlice());
      dispatch(resetOpenCardDetail());
    };
    // selectedPaidolId is constant for the lifetime of this form — run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 8. Populate cardName and address when a user is selected from the cardholder autocomplete
  useEffect(() => {
    if (selectedUser) {
      const { firstName, lastName } = populateAddressFromUser(selectedUser);
      setValue('cardName', `${firstName} ${lastName}`.trim());
    }
  }, [selectedUser, populateAddressFromUser, setValue]);

  const handleContinueWithWarning = useCallback(() => {
    if (pendingFormValues) {
      onSubmit(pendingFormValues);
      setPendingFormValues(null);
    }
    setIsMonthlyLimitWarningOpen(false);
  }, [pendingFormValues, onSubmit]);

  const handleCloseWarning = useCallback(() => {
    setIsMonthlyLimitWarningOpen(false);
    setPendingFormValues(null);
  }, []);

  const handleCloseBalanceResetWarning = useCallback(() => {
    setIsBalanceResetWarningOpen(false);
    setPendingFormValues(null);
  }, []);

  const handleContinueWithBalanceResetWarning = useCallback(() => {
    if (pendingFormValues) {
      onSubmit(pendingFormValues);
      setPendingFormValues(null);
    }
    setIsBalanceResetWarningOpen(false);
  }, [pendingFormValues, onSubmit]);

  // Clear non-USPS selection on PO-box detection — the picker's existing required-field
  // validation then surfaces the red border, no separate warning needed.
  useEffect(() => {
    if (!isAddressValidationEnabled) return;
    if (!isPoBox) return;
    const current = getValues('shippingMethod');
    if (current && current !== NO_SHIPPING && !isUspsShippingMethod(current)) {
      setValue('shippingMethod', NO_SHIPPING, { shouldValidate: true });
    }
  }, [isPoBox, isAddressValidationEnabled, getValues, setValue]);

  const [correctionDeclined, setCorrectionDeclined] = useState(false);

  // Reset the dismissed state whenever the underlying validation status changes away
  // from with_changes (e.g., the user edited the address and it re-validated).
  useEffect(() => {
    if (validation.status.kind !== 'with_changes') {
      setCorrectionDeclined(false);
    }
  }, [validation.status.kind]);

  const applyAddressCorrection = useCallback(() => {
    if (validation.status.kind !== 'with_changes') return;
    const { corrected } = validation.status;
    setValue('addressStreet1', corrected.streetAddress, { shouldValidate: true });
    setValue('addressStreet2', corrected.extendedAddress ?? '', { shouldValidate: true });
    setValue('addressZip', corrected.postalCode, { shouldValidate: true });
    setValue('addressCity', corrected.locality, { shouldValidate: true });
    setValue('addressState', corrected.region, { shouldValidate: true });
    validation.resolveCorrections('accept');
  }, [validation, setValue]);

  const handleAddressCorrectionDecision = useCallback(
    (decision: 'accept' | 'decline') => {
      if (validation.status.kind !== 'with_changes') return;
      if (decision === 'accept') {
        applyAddressCorrection();
      } else {
        // Keep the hook's status at with_changes so submit stays blocked. We only hide
        // the dialog locally and surface an inline affordance to apply the suggestion.
        setCorrectionDeclined(true);
      }
    },
    [validation, applyAddressCorrection]
  );

  const onClickSave: SubmitHandler<NewCardFormValues> = async (data) => {
    // SEE-946: hard-stop if the typed/selected cardholder is a Secondary
    // admin. The dropdown filter excludes them but the email field accepts
    // free-form text — match the typed email against the loaded users list
    // so a manual entry is also caught before we fire issuePaymentCard.
    // Backend issue_card.py also returns 403, so the snackbar copy is the
    // same end-to-end.
    const targetEmail = (data.cardHolder || '').toLowerCase();
    const targetUser = targetEmail
      ? users.find((u) => u?.email && u.email.toLowerCase() === targetEmail)
      : undefined;
    const targetRole = selectedUser?.roles ?? targetUser?.roles;
    if (targetRole === Role.SECONDARY_ADMINISTRATOR) {
      enqueueSnackbar(t('assignCard.secondaryAdminBlocked'), { variant: 'warning' });
      return;
    }

    if (isAddressValidationEnabled && shouldDisplayMailing) {
      const final = await validation.validateOnSubmit();
      if (final.kind === 'with_changes') {
        // Re-open the dialog if the user previously declined and is trying to save again.
        setCorrectionDeclined(false);
        return;
      }
      if (final.kind === 'incomplete' || final.kind === 'invalid' || final.kind === 'transport_error') {
        return;
      }
    }

    // If converting from MSR to ISR, show balance reset warning first
    if (isConvertingFromMsrToIsr) {
      setPendingFormValues(data);
      setIsBalanceResetWarningOpen(true);
      return;
    }

    // If updating monthly spend limit, show warning first
    if (isUpdatingMonthlyLimit) {
      setPendingFormValues(data);
      setIsMonthlyLimitWarningOpen(true);
      return;
    }

    // Otherwise, submit directly
    return onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* First Col (Basics) */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Basics */}
          <Grid xs={12}>
            <Box display="flex" alignItems="center">
              <CreditCardOutlinedIcon sx={{ width: 26, height: 26, mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" color="primary.main">
                {t('basics')}
              </Typography>
            </Box>
          </Grid>

          {
            <>
              {/* Email description */}
              <Grid xs={12}>
                <Typography variant="medium">
                  <b>{t('enterCardholdersEmail')}</b>
                </Typography>
              </Grid>

              {/* Cardholder */}
              <Grid xs={12}>
                <Controller
                  name="cardHolder"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete<PaidolUser>
                      disabled={formType === FormType.OrderPhysical || formType === FormType.UpdateCard}
                      ListboxProps={sentryMask}
                      slotProps={{ popper: sentryMask }}
                      options={assignableUsers}
                      getOptionLabel={(option) => option.email || ''}
                      isOptionEqualToValue={(option, value) => option.email === value.email}
                      value={
                        selectedUser ??
                        users.find((u) => u.email === field.value) ??
                        (field.value ? ({ email: field.value } as PaidolUser) : null)
                      }
                      onChange={(_, newValue) => {
                        if (newValue !== null) {
                          setSelectedUser(newValue);
                          field.onChange(newValue.email);
                        } else {
                          setSelectedUser(undefined);
                          field.onChange('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          type="text"
                          error={!!fieldState.error || !!errors.cardHolder}
                          helperText={fieldState.error?.message || errors?.cardHolder?.message}
                          label={t('cardholder')}
                          variant="outlined"
                          required
                          fullWidth
                          disabled={formType === FormType.OrderPhysical || formType === FormType.UpdateCard}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccountCircleOutlinedIcon />
                              </InputAdornment>
                            ),
                            inputProps: {
                              ...params.inputProps,
                              autoComplete: 'off',
                              'data-lpignore': 'true',
                              'data-form-type': 'other',
                              name: cardholderInputNameRef.current,
                              style: { paddingLeft: '8px' },
                              ...sentryMask,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </>
          }

          {
            <>
              {/* Card name description */}
              <Grid xs={12}>
                <Typography variant="medium">
                  <b>{t('cardholderPrintedOnPhysicalCards')} </b>
                  {t('cardholderNameHelp')}
                </Typography>
              </Grid>

              {/* Card name */}
              <Grid xs={12}>
                <Controller
                  name="cardName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="text"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      label={t('Card Name')}
                      id="cardName"
                      variant="outlined"
                      required
                      fullWidth
                      onBlur={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCardOutlinedIcon />
                          </InputAdornment>
                        ),
                        inputProps: {
                          style: { paddingLeft: '8px' },
                          ...sentryMask,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </>
          }

          {/* Card type */}
          <Grid xs={6}>
            <Controller
              name="cardType"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!errors.cardType}
                  disabled={formType === FormType.OrderPhysical || formType === FormType.UpdateCard}
                >
                  <InputLabel id="cardType-label">{t('cardType')}</InputLabel>
                  <Select
                    {...field}
                    id="cardType"
                    labelId="cardType-label"
                    label={t('cardType')}
                    startAdornment={
                      <InputAdornment position="start">
                        <CreditCardOutlinedIcon />
                      </InputAdornment>
                    }
                  >
                    {Object.entries(CARD_TYPES).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('virtualCardsUsage')}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Assign to group */}
          <Grid xs={6}>
            <Controller
              name="cardGroupId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={formType === FormType.OrderPhysical}>
                  <InputLabel id="cardGroupId-label">{t('assignToGroup')}</InputLabel>
                  <Select
                    {...field}
                    id="cardGroupId"
                    labelId="cardGroupId-label"
                    label={t('assignToGroup')}
                    startAdornment={
                      <InputAdornment position="start">
                        <GroupsIcon />
                      </InputAdornment>
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {(formType === FormType.UpdateCard || userIsAdministrator) && (
                      <MenuItem value={NO_GROUP}>{t('noGroup')}</MenuItem>
                    )}
                    {selectedGroup ? (
                      <MenuItem value={selectedGroup.id}>{selectedGroup.name}</MenuItem>
                    ) : (
                      groups
                        ?.filter((group): group is CardGroup => !!group)
                        .map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>

        {/* Second Col (Mailing) */}
        {shouldDisplayMailing && (
          <Grid container spacing={2} sx={{ pb: 2, mt: 1 }} {...sentryMask}>
            {/* Physical card mailing. Recipient name + address are masked by the container (SEE-1834). */}
            <Grid xs={12}>
              <Box display="flex" alignItems="center">
                <LocalShippingOutlinedIcon sx={{ width: 26, height: 26, mr: 1, color: 'primary.main' }} />
                <Typography variant="h4" color="primary.main">
                  {t('physicalCardMailing')}
                </Typography>
              </Box>
            </Grid>

            {/* Address */}
            <Grid xs={12}>
              <Typography variant="medium" className={REPLAY_CHROME_CLASS}>
                <b>{t('address')}</b>
              </Typography>
            </Grid>

            {/* First name */}
            <Grid xs={6}>
              <Controller
                name="addressFirstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressFirstName}
                    label={t('firstName')}
                    id="addressFirstName"
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={(e) => field.onChange(formatName(e.target.value))}
                  />
                )}
              />
            </Grid>

            {/* Last name */}
            <Grid xs={6}>
              <Controller
                name="addressLastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressLastName}
                    label={t('lastName')}
                    id="addressLastName"
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={(e) => field.onChange(formatName(e.target.value))}
                  />
                )}
              />
            </Grid>

            {/* Street 1 */}
            <Grid xs={12} xl={6}>
              <Controller
                name="addressStreet1"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressStreet1}
                    label={t('streetAddress')}
                    id="addressStreet1"
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={(e) => {
                      field.onChange(formatText(e.target.value));
                      if (isAddressValidationEnabled) validation.validate();
                    }}
                  />
                )}
              />
              {containsSecondaryDetails(watchedStreet1) && (
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{ mt: 1 }}
                  className={REPLAY_CHROME_CLASS}
                >
                  {t('addressLine1HasSecondaryDetails')}
                </Typography>
              )}
            </Grid>

            {/* Street 2 */}
            <Grid xs={12} xl={6}>
              <Controller
                name="addressStreet2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressStreet2}
                    label={t('apartmentSuiteEtc')}
                    id="addressStreet2"
                    variant="outlined"
                    fullWidth
                    onBlur={(e) => {
                      field.onChange(formatText(e.target.value));
                      if (isAddressValidationEnabled) validation.validate();
                    }}
                  />
                )}
              />
            </Grid>

            {/* City */}
            <Grid xs={12} xl={4}>
              <Controller
                name="addressCity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressCity}
                    label={t('city')}
                    id="addressCity"
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={(e) => {
                      field.onChange(formatText(e.target.value));
                      if (isAddressValidationEnabled) validation.validate();
                    }}
                  />
                )}
              />
            </Grid>

            {/* State */}
            <Grid xs={6} xl={4}>
              <Controller
                name="addressState"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.addressState}>
                    <InputLabel id="addressState-label">{`State`}</InputLabel>
                    <Select
                      {...field}
                      id="addressState"
                      labelId="addressState-label"
                      label="State"
                      onChange={(e) => {
                        field.onChange(e);
                        if (isAddressValidationEnabled) validation.validate();
                      }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state.abbreviation} value={state.abbreviation}>
                          {state.name} ({state.abbreviation})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* ZIP */}
            <Grid xs={6} xl={4}>
              <Controller
                name="addressZip"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.addressZip}
                    label={t('zip')}
                    id="addressZip"
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={(e) => {
                      field.onChange(sanitizePostalCode(e.target.value));
                      if (isAddressValidationEnabled) validation.validate();
                    }}
                  />
                )}
              />
            </Grid>

            {isAddressValidationEnabled &&
              (validation.status.kind === 'incomplete' || validation.status.kind === 'invalid') && (
                <Grid xs={12}>
                  <Typography variant="body2" color="error" sx={{ mt: 1 }} className={REPLAY_CHROME_CLASS}>
                    {formatComponentMessage(validation.status, ADDRESS_COMPONENT_LABELS)}
                  </Typography>
                </Grid>
              )}
            {isAddressValidationEnabled && validation.status.kind === 'transport_error' && (
              <Grid xs={12}>
                <Typography variant="body2" color="error" sx={{ mt: 1 }} className={REPLAY_CHROME_CLASS}>
                  {t('addressValidationTransportError')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    validation.validate();
                  }}
                >
                  {t('retry')}
                </Button>
              </Grid>
            )}
            {isAddressValidationEnabled &&
              validation.status.kind === 'with_changes' &&
              correctionDeclined && (
                <Grid xs={12}>
                  <Typography variant="body2" color="error" sx={{ mt: 1 }} className={REPLAY_CHROME_CLASS}>
                    {t('addressNotVerified')}
                  </Typography>
                  <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={applyAddressCorrection}>
                    {t('useSuggestedAddress')}
                  </Button>
                </Grid>
              )}

            {/* Delivery */}
            <Grid xs={12} sx={{ mt: 2 }}>
              <Typography variant="medium" className={REPLAY_CHROME_CLASS}>
                <b>{t('delivery')}</b>
              </Typography>
            </Grid>

            {/* Require signature */}
            <Grid xs={12}>
              <Controller
                control={control}
                name="requireSignature"
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={<Switch {...field} />}
                      checked={!!field.value}
                      label={t('requireSignatureForDelivery')}
                    />
                  );
                }}
              />
            </Grid>

            {/* Shipping method */}
            <Grid xs={12}>
              <Controller
                name="shippingMethod"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.shippingMethod}>
                    <InputLabel id="shippingMethod-label">{t('shippingMethod')}</InputLabel>
                    <Select
                      {...field}
                      id="shippingMethod"
                      labelId="shippingMethod-label"
                      label={t('shippingMethod')}
                      startAdornment={
                        <InputAdornment position="start">
                          <LocalShippingOutlinedIcon />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value={NO_SHIPPING}>{`No method selected`}</MenuItem>
                      {Object.entries(activeShippingOptions).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        )}

        {/* Third Col (Rules and limits) */}
        <Grid xs={12} md={shouldDisplayMailing ? 4 : 6}>
          {/* Spend rules section */}
          <Box
            sx={{
              mb: 2,
              backgroundColor: 'white',
              borderRadius: 2,
              padding: 1.5,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            {/* Spend rules header */}
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <RuleIcon sx={{ width: 26, height: 26, mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" color="primary.main">
                Spend rules
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Monthly Budget */}
              <Grid xs={4}>
                <Box>
                  <Controller
                    name="monthlySpendLimit"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="monthlySpendLimit"
                        type="number"
                        label={t('monthlySpendLimit')}
                        required={!unlimitedSpendingWatch}
                        value={!unlimitedSpendingWatch ? getValues('monthlySpendLimit') ?? '' : ''}
                        error={!unlimitedSpendingWatch && !!errors.monthlySpendLimit}
                        helperText={errors?.monthlySpendLimit?.message}
                        placeholder={unlimitedSpendingWatch ? 'No limit' : ''}
                        variant="outlined"
                        fullWidth
                        disabled={
                          formType === FormType.OrderPhysical ||
                          !userIsAdministrator ||
                          unlimitedSpendingWatch ||
                          isMerchantSpendRuleSelected
                        }
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(/[.,eE+\-\s]/g, '');
                          field.onChange(sanitized);
                        }}
                        onKeyDown={(e) => {
                          if (['.', ',', 'e', 'E', '+', '-', ' '].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const paste = e.clipboardData.getData('text');
                          const sanitized = paste.replace(/,/g, '').split(/[.]/)[0];
                          if (sanitized && /^\d+$/.test(sanitized)) {
                            field.onChange(sanitized);
                          }
                        }}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            step: 1,
                            autoComplete: 'off',
                            autoCorrect: 'off',
                            spellCheck: 'false',
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <BudgetIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />

                  <Box sx={{ p: 1 }}>
                    <Controller
                      control={control}
                      name="isMonthlyBudgetUnlimited"
                      render={({ field }) => (
                        <FormControlLabel
                          label={t('unlimited')}
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                              disabled={!userIsAdministrator || isMerchantSpendRuleSelected}
                              onChange={(e) => {
                                const isUnlimited = e.target.checked;
                                setValue('isMonthlyBudgetUnlimited', isUnlimited);
                                setValue(
                                  'monthlySpendLimit',
                                  isUnlimited ? undefined : getValues('monthlySpendLimit')
                                );
                                if (isUnlimited) {
                                  clearErrors('monthlySpendLimit');
                                }
                              }}
                            />
                          }
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Maximum transaction amount */}
              <Grid xs={4}>
                <Box>
                  <Controller
                    name="maximumTransactionAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="maximumTransactionAmount"
                        type="number"
                        label="Maximum transaction amount"
                        required={!unlimitedTransactionWatch}
                        value={!unlimitedTransactionWatch ? getValues('maximumTransactionAmount') ?? '' : ''}
                        error={!unlimitedTransactionWatch && !!errors.maximumTransactionAmount}
                        helperText={errors?.maximumTransactionAmount?.message}
                        placeholder={unlimitedTransactionWatch ? 'No maximum' : ''}
                        variant="outlined"
                        fullWidth
                        disabled={
                          formType === FormType.OrderPhysical ||
                          !userIsAdministrator ||
                          unlimitedTransactionWatch ||
                          isMerchantSpendRuleSelected
                        }
                        onChange={(e) => {
                          const sanitized = e.target.value.replace(/[.,eE+\-\s]/g, '');
                          field.onChange(sanitized);
                        }}
                        onKeyDown={(e) => {
                          if (['.', ',', 'e', 'E', '+', '-', ' '].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const paste = e.clipboardData.getData('text');
                          const sanitized = paste.replace(/,/g, '').split(/[.]/)[0];
                          if (sanitized && /^\d+$/.test(sanitized)) {
                            field.onChange(sanitized);
                          }
                        }}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            step: 1,
                            autoComplete: 'off',
                            autoCorrect: 'off',
                            spellCheck: 'false',
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <BudgetIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />

                  <Box sx={{ p: 1 }}>
                    <Controller
                      control={control}
                      name="isMaximumTransactionAmountUnlimited"
                      render={({ field }) => (
                        <FormControlLabel
                          label={t('unlimited')}
                          control={
                            <Switch
                              {...field}
                              checked={field.value}
                              disabled={!userIsAdministrator || isMerchantSpendRuleSelected}
                              onChange={(e) => {
                                const isUnlimited = e.target.checked;
                                setValue('isMaximumTransactionAmountUnlimited', isUnlimited);
                                setValue(
                                  'maximumTransactionAmount',
                                  isUnlimited ? undefined : getValues('maximumTransactionAmount')
                                );
                                if (isUnlimited) {
                                  clearErrors('maximumTransactionAmount');
                                }
                              }}
                            />
                          }
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Spend rule */}
              <Grid xs={4}>
                <Controller
                  name="merchantSpendRuleId"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      error={!!errors.merchantSpendRuleId}
                      disabled={formType === FormType.OrderPhysical || !userIsAdministrator}
                    >
                      <InputLabel id="merchantSpendRuleId-label">{`Spend rule`}</InputLabel>
                      <Select
                        {...field}
                        id="merchantSpendRuleId"
                        labelId="merchantSpendRuleId-label"
                        label="Spend rule"
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <RuleIcon />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value={NO_RULE}>{`No rule applied`}</MenuItem>
                        {rules.map((rule) => (
                          <MenuItem key={rule.id} value={rule.id}>
                            {rule.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Categories and Countries - Always show, but disable when merchant rule is attached/selected */}
              <Grid xs={12} md={6}>
                <SpendingByCategory control={control} isDisabled={isMerchantSpendRuleSelected} />
              </Grid>
              <Grid xs={12} md={6}>
                <SpendingByCountry control={control} isDisabled={isMerchantSpendRuleSelected} />
              </Grid>
            </Grid>
          </Box>

          {/* Trello card: 489
              We re currently creating a new spend control every time a card is issued which needs to be reworked.
              Lets remove the option to add the "require address" spend control from the issue card side sheet until we rework the spend controls. */}
        </Grid>

        <ResponsiveButtonContainer>
          <>
            {cancelText ? (
              <Button onClick={onCancel} variant="text" size="small" disabled={loading}>
                {cancelText}
              </Button>
            ) : (
              <Button onClick={onCancel} variant="outlined" size="small" disabled={loading}>
                {t('cancel')}
              </Button>
            )}
            <ProgressButton
              loading={loading}
              disabled={loading || isInitialDataLoading}
              size="small"
              onClick={handleSubmit(onClickSave)}
            >
              {formType === FormType.UpdateCard ? t('saveChanges') : t('issueCard')}
            </ProgressButton>
          </>
        </ResponsiveButtonContainer>

        {/* Warning Modal for Monthly Spend Limit Updates */}
        <WarningModal
          isLoading={!!loading}
          isOpen={isMonthlyLimitWarningOpen}
          onClose={handleCloseWarning}
          onContinue={handleContinueWithWarning}
          modalHeader={t('spendRules.monthlyLimitUpdateWarning.header')}
          continueButtonText={t('spendRules.monthlyLimitUpdateWarning.continue')}
        >
          <Typography variant="body1" color="text.primary">
            {t('spendRules.monthlyLimitUpdateWarning.message', {
              newLimit: (() => {
                const actualMonthlyLimit = pendingFormValues?.monthlySpendLimit;
                if (typeof actualMonthlyLimit === 'string') {
                  // For string inputs, preserve the original precision
                  return actualMonthlyLimit;
                } else if (typeof actualMonthlyLimit === 'number') {
                  return actualMonthlyLimit.toString();
                }
                return '0.00';
              })(),
            })}
          </Typography>
        </WarningModal>

        {/* Warning Modal for Balance Reset (MSR to ISR conversion) */}
        <WarningModal
          isLoading={!!loading}
          isOpen={isBalanceResetWarningOpen}
          onClose={handleCloseBalanceResetWarning}
          onContinue={handleContinueWithBalanceResetWarning}
          modalHeader={t('spendRules.balanceResetWarning.header')}
          continueButtonText={t('spendRules.balanceResetWarning.continue')}
        >
          <Typography variant="body1" color="text.primary">
            {t('spendRules.balanceResetWarning.message')}
          </Typography>
        </WarningModal>

        {validation.status.kind === 'with_changes' && !correctionDeclined && (
          <AddressCorrectionDialog
            open
            original={validation.status.original}
            corrected={validation.status.corrected}
            onResolve={handleAddressCorrectionDecision}
          />
        )}
      </form>
    </FormProvider>
  );
}

export default NewCardForm;
